import type { FC } from 'react';
import { useCallback, useMemo } from 'react';

import { useGameConfig } from '../../../../config/GameConfigProvider';
import { useCashierCallbackConnected } from '../../../../hooks/useCashierCallbackConnected';
import { useExitCallbackConnected } from '../../../../hooks/useExitCallbackConnected';
import { useIsLobbyButtonHiddenConnected } from '../../../../hooks/useIsLobbyButtonHiddenConnected';
import { useShouldStayConnected } from '../../../../hooks/useShouldStayConnected';
import { Logger } from '../../../../logger/Logger';
import { useCoreDispatch } from '../../../../store/CoreStore';
import { useChatSlice } from '../../../../store/slices/chat/selectors';
import { useConfigSlice } from '../../../../store/slices/config/selectors';
import { useHeaderSlice } from '../../../../store/slices/header/selectors';
import { headerActions } from '../../../../store/slices/header/slice';
import { popupActions } from '../../../../store/slices/popup/slice';
import { redirectParentIframe } from '../../../../utils/redirect';
import { PopupType } from '../../../popup/types';
import { NavPages } from '../../navPages/NavPagesConnected';
import type { ClickCallback, NavDrawerHomeSlideButtonCallbacks, NavDrawerHomeSlideLayoutConfig } from '../types';

import { NavDrawerHomeSlide } from './NavDrawerHomeSlide';

const logHomeButtonClickEvent = (eventName: string): void => Logger.logEvent(eventName);

export const NavDrawerHomeSlideConnected: FC = () => {
  const isNavDrawerOpen = useHeaderSlice.getNavOpen();
  const dispatch = useCoreDispatch();
  const cashierCallback = useCashierCallbackConnected();
  const exitCallback = useExitCallbackConnected();
  const isLobbyButtonHidden = useIsLobbyButtonHiddenConnected();
  const shouldStayAtTable = useShouldStayConnected();
  const { quickGuideContent, betLimitContent } = useGameConfig();

  const exitUrl = useConfigSlice.casinoConfig()?.exitUrl;
  const isChatDisabled = useConfigSlice.isChatDisabled();
  const isSupportChatDisabled = useConfigSlice.isSupportChatDisabled();
  const isPrivateTable = useConfigSlice.isPrivateTable();
  const isLiveSupportRead = useChatSlice.getIsLiveSupportRead();
  const isLobbyOpen = useHeaderSlice.getIsMiniLobbyOpen();
  const unreadPrivateMessageCount = useChatSlice.getUnreadPrivateMessageCount();
  const isChatDisabledForJurisdiction = useConfigSlice.getIsChatDisabledForJurisdiction();

  const layoutConfig: NavDrawerHomeSlideLayoutConfig = useMemo(() => {
    return {
      lobby: isLobbyButtonHidden,
      cashier: Boolean(cashierCallback),
      chat: !isPrivateTable && (isChatDisabledForJurisdiction || isChatDisabled),
      quickGuide: Boolean(quickGuideContent),
      liveSupport: !isSupportChatDisabled,
      home: Boolean(exitUrl),
      betLimits: Boolean(betLimitContent),
    };
  }, [
    isLobbyButtonHidden,
    cashierCallback,
    isPrivateTable,
    isChatDisabledForJurisdiction,
    isChatDisabled,
    quickGuideContent,
    isSupportChatDisabled,
    exitUrl,
    betLimitContent,
  ]);

  const lobbyClick: ClickCallback<HTMLElement> = useCallback(
    (event): void => {
      event?.preventDefault();
      event?.stopPropagation();

      if (!isLobbyOpen) {
        logHomeButtonClickEvent('openLobbyMobMenu');
        dispatch(
          shouldStayAtTable
            ? popupActions.pushPopup(PopupType.DontLeaveRoundInProgress)
            : headerActions.setMiniLobbyOpen(true)
        );
      }
    },
    [shouldStayAtTable, dispatch, isLobbyOpen]
  );

  const chatClick = useCallback(() => {
    logHomeButtonClickEvent('openChatMobMenu');
    dispatch(headerActions.pushNavRoute(NavPages.CHAT));
  }, [dispatch]);

  const historyClick = useCallback(() => {
    logHomeButtonClickEvent('openHistoryMobMenu');
    dispatch(headerActions.pushNavRoute(NavPages.HISTORY));
  }, [dispatch]);

  const quickGuideClick = useCallback(() => {
    logHomeButtonClickEvent('openQuickGuideMobMenu');
    dispatch(headerActions.pushNavRoute(NavPages.QUICK_GUIDE));
  }, [dispatch]);

  const betLimitsClick = useCallback(() => {
    logHomeButtonClickEvent('openBetLimitsMobMenu');
    dispatch(headerActions.pushNavRoute(NavPages.BET_LIMITS));
  }, [dispatch]);

  const settingsClick = useCallback(() => {
    logHomeButtonClickEvent('openSettingsMobMenu');
    dispatch(headerActions.setSettingsTabIndex(0));
    dispatch(headerActions.pushNavRoute(NavPages.SETTINGS));
  }, [dispatch]);

  const helpClick = useCallback(() => {
    logHomeButtonClickEvent('openHelpMobMenu');
    dispatch(headerActions.pushNavRoute(NavPages.HELP));
  }, [dispatch]);

  const liveSupportClick = useCallback(() => {
    logHomeButtonClickEvent('openSupportChatMobMenu');
    dispatch(headerActions.pushNavRoute(NavPages.SUPPORT));
  }, [dispatch]);

  const homeClick = useCallback(() => {
    logHomeButtonClickEvent('openHomeMobMenu');
    if (shouldStayAtTable) {
      dispatch(popupActions.pushPopup(PopupType.DontLeaveRoundInProgress));
      return;
    }
    exitCallback();
    if (exitUrl) redirectParentIframe(exitUrl);
  }, [dispatch, exitCallback, exitUrl, shouldStayAtTable]);

  const buttonCallbacks: NavDrawerHomeSlideButtonCallbacks = useMemo(() => {
    return {
      lobbyClick,
      cashierClick: cashierCallback,
      chatClick,
      betLimitsClick,
      helpClick,
      historyClick,
      settingsClick,
      homeClick,
      liveSupportClick,
      quickGuideClick,
    };
  }, [
    betLimitsClick,
    cashierCallback,
    chatClick,
    helpClick,
    historyClick,
    homeClick,
    liveSupportClick,
    lobbyClick,
    settingsClick,
    quickGuideClick,
  ]);
  return (
    <NavDrawerHomeSlide
      layoutConfig={layoutConfig}
      buttonCallbacks={buttonCallbacks}
      isNavDrawerOpen={isNavDrawerOpen}
      showLiveSupportReadBadge={!isLiveSupportRead}
      isPrivateTable={isPrivateTable}
      unreadPrivateMessageCount={unreadPrivateMessageCount}
    />
  );
};
